<alert-dialog
  [isOpen]="!!order"
  title="Order Details"
  dimensionType="wide"
  (onConfirm)="order = null"
  (onCancel)="onClose.emit()"
>
  @if (order) {
    @let createdBy = createdBy$ | async;
    @let lastUpdatedBy = lastUpdatedBy$ | async;
    @let stocks = stocks$ | async;

    <div id="print-section">
      <div class="flex flex-col gap-6 text-base-content">
        <div class="flex flex-col gap-2 items-start border-b border-base-300 pb-4">
          <div class="flex w-full justify-between items-center">
            <h3 class="text-xs font-bold uppercase tracking-wide text-base-content/50">Order ID</h3>
            <p class="font-mono text-xl font-bold text-end">{{ order.id }}</p>
          </div>
          <div class="flex w-full justify-between items-center">
            <h3 class="text-xs font-bold uppercase tracking-wide text-base-content/50 mb-1">Status</h3>
            <div class="flex items-center gap-2">
              @if (order.urgent) {
                <span class="badge badge-lg badge-error font-semibold">URGENT</span>
              }
              <span
                class="badge badge-lg font-semibold"
                [class.badge-success]="order.status === 'APPROVED'"
                [class.badge-warning]="order.status === 'PENDING'"
                [class.badge-error]="order.status === 'REJECTED'"
                [class.badge-ghost]="order.status === 'CANCELLED'"
              >
              {{ order.status }}
            </span>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="p-3 bg-base-200/50 rounded-lg">
            <h3 class="text-xs font-bold uppercase text-base-content/60">Customer</h3>
            @if (order.customerId) {
              <a class="link link-hover font-medium truncate block"
                 title="{{ order.customerName }}"
                 [routerLink]="['/customers', order.customerId]"
                 target="_blank" rel="noopener">
                {{ order.customerName || '—' }}
              </a>
            } @else {
              <p class="font-medium truncate" title="{{ order.customerName }}">
                {{ order.customerName || '—' }}
              </p>
            }
          </div>

          <div class="p-3 bg-base-200/50 rounded-lg">
            <h3 class="text-xs font-bold uppercase text-base-content/60">Order Type</h3>
            <p class="font-medium uppercase">{{ order.type }}</p>
          </div>
        </div>

        <div>
          <h3 class="text-sm font-bold text-base-content/70 mb-2">Items</h3>

          @if (!hasStock) {
            <div class="alert alert-warning shadow-lg">
              <div>
                <span class="font-medium">No items found for this order.</span>
              </div>
            </div>
          } @else if (!stocks) {
            <div class="skeleton h-20 w-full rounded-md"></div>
          } @else {
            <div class="overflow-x-auto border border-base-300 rounded-lg">
              <table class="table table-sm w-full">
                <thead class="bg-base-200 text-base-content/70">
                <tr>
                  <th>Item Code</th>
                  <th>Description</th>
                  <th class="text-right">Available</th>
                  <th class="text-right">Qty</th>
                  <th class="text-right">Unit Price</th>
                  <th class="text-right">Price</th>
                </tr>
                </thead>
                <tbody>
                  @for (stock of stocks; track stock.id) {
                    <tr>
                      <td class="font-mono font-bold">{{ stock.id }}</td>
                      <td>{{ stock.description }}</td>
                      <td class="text-right font-mono">
                        @if (order.type === "ORDER") {
                          <div class="tooltip tooltip-left" data-tip="Excludes current order qty">
                          <span class="cursor-help decoration-dotted underline decoration-base-content/30">
                            {{ stock.stock }}
                          </span>
                          </div>
                        } @else {
                          {{ stock.stock }}
                        }
                      </td>

                      <td class="text-right font-bold">
                        {{ stock.quantity }}
                      </td>

                      <td class="text-right font-mono">
                        {{ stock.price | currency:'LKR' }}
                      </td>

                      <td class="text-right font-bold">
                        {{ (stock.price * stock.quantity) | currency:'LKR' }}
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            <div class="flex justify-between items-center mt-3 px-1">
              <span class="text-sm font-medium text-base-content/60">Estimated Total</span>
              <span class="text-2xl font-bold text-primary">
              {{ calculateTotalPrice(stocks) | currency:'LKR' }}
            </span>
            </div>
          }
        </div>

        <div class="text-xs text-base-content/60 bg-base-100 rounded-md border border-base-200 p-3 space-y-1">
          <div class="flex justify-between">
            <span>Created By:</span>
            <span class="font-medium">
            {{ createdBy ? createdBy.name : 'Loading...' }}
          </span>
          </div>
          @if (lastUpdatedBy) {
            <div class="flex justify-between border-t border-base-200 pt-1 mt-1">
              <span>Last Updated:</span>
              <span class="font-medium truncate max-w-50">
              {{ getLastUpdatedByDeconstructed(lastUpdatedBy) }}
            </span>
            </div>
          }
        </div>
      </div>
    </div>

    <div class="modal-action mt-2">
      <form method="dialog" class="flex gap-2 w-full justify-end">
        @if (order.status === 'PENDING') {
          <button type="button" (click)="closeModal(true)" class="btn btn-ghost">
            Cancel
          </button>
          <ng-container *ngTemplateOutlet="printButton"/>
          <button type="button" (click)="reject()" class="btn btn-outline btn-error">
            Reject
          </button>
          <button type="button" (click)="approve()" class="btn btn-success text-white">
            Approve Order
          </button>
        } @else {
          <button (click)="closeModal(false)" class="btn btn-primary">
            Close
          </button>
          <ng-container *ngTemplateOutlet="printButton"/>
        }
      </form>
    </div>

    <ng-template #printButton>
      <button
        ngxPrint
        type="button"
        class="btn btn-outline"
        printSectionId="print-section"
        [useExistingCss]="true"
        [printTitle]="`${order.customerName} - ${getLastUpdatedByDeconstructed(lastUpdatedBy || 'system')} - ${order.id}`"
      >
        Print
      </button>
    </ng-template>

  }
</alert-dialog>
